"use client"
import { useEffect, useState } from "react"
import { Box, Button, Grid, Typography } from "@mui/material"
import CustomSelect from "../../../components/multi-select/SelectField"
import GenericTable from "../../../components/table"
import { ProjectApi, UserApi } from "../../../apis"
import WeekPicker from "../../user/phases/weekPicker"
import dayjs from "dayjs"
import { convertDateFormatShortYear } from "../../../utils/helpers"

function TimeReport() {
  const columns = [
    {
      field: "weekStart",
      headerName: "Week Start",
      width: 120,
      sortable: true,
      renderCell: (params) => (
        <div>
          {convertDateFormatShortYear(params.value)}
        </div>
      ),
    },
    { field: "weekEnd", headerName: "Week End", width: 120, sortable: true ,
      renderCell: (params) => (
        <div>
          {convertDateFormatShortYear(params.value)}
        </div>
      ),
    },
    { field: "user_name", headerName: "User", width: 150, sortable: true },
    { field: "totalHours", headerName: "Total Hours", width: 120, sortable: true },
    {
      field: "projectDetails",
      headerName: "Projects & Phases",
      width: 300,
      sortable: false,
      renderCell: (params) => (
        <div
          style={{
            whiteSpace: "pre-line",
            fontWeight: "bold",
            color: "#1976d2",
            fontSize: "14px",
          }}
        >
          {params.value.split(", ").map((project, index) => (
            <div key={index}>• {project}</div>
          ))}
        </div>
      ),
    },
    { field: "status", headerName: "Status", width: 120, sortable: true },
    { field: "notes", headerName: "Notes", width: 200, sortable: true },
  ]

  const [projects, setProjects] = useState([])
  const [filter, setFilter] = useState("")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [reportData, setReportData] = useState([])
  const [selectedProject, setSelectedProject] = useState("")
  const [user, setUser] = useState([])
  const [selectedUser, setSelectedUser] = useState("")
  const [selectedDate, setSelectedDate] = useState(dayjs())

  const filterOptions = [
    { value: "project", label: "Project" },
    { value: "date", label: "Date" },
    { value: "user", label: "User" },
  ]

  const handleChange = (event) => {
    setFilter(event.target.value)
    setSelectedProject("")
    setSelectedUser("")
    setSelectedDate(dayjs())
  }

  const fetchUsers = async () => {
    try {
      const res = await UserApi.getAllUsers()
      const userOptions = res.data.data.users.map((user) => ({
        value: user.id,
        label: user.name,
      }))
      setUser(userOptions)
    } catch (error) {
      console.error("Error fetching users:", error)
      setUser([])
    } finally {
      setLoading(false)
    }
  }

  const fetchProjects = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await ProjectApi.getAllProjects()
      const projectOptions = res.data.data.projects.map((proj) => ({
        value: proj.name,
        label: proj.name,
      }))
      setProjects(projectOptions)
    } catch (error) {
      console.error("Error fetching projects:", error)
      setError("Failed to load projects. Please try again later.")
      setProjects([])
    } finally {
      setLoading(false)
    }
  }

  const handleProjectChange = (event) => {
    setSelectedProject(event.target.value)
  }

  const handleUserChange = (event) => {
    setSelectedUser(event.target.value)
  }

  const handleDateChange = (date) => {
    setSelectedDate(date)

    // Calculate week start (Monday) and week end (Sunday)
    const weekStart = date.startOf("week").format("DD-MM-YYYY") // Monday
    const weekEnd = date.endOf("week").format("DD-MM-YYYY") // Sunday

    // Update the date range for API call
    fetchReportWithDateRange(weekStart, weekEnd)
  }

  const fetchReportWithDateRange = async (weekStart, weekEnd) => {
    try {
      setLoading(true)
      const params = {
        weekStart,
        weekEnd,
      }

      if (selectedUser) params.userId = selectedUser
      if (selectedProject) params.projectName = selectedProject

      const response = await UserApi.getReportByProject({ params })

      // Grouping logic
      const groupedData = response.data.data.reduce((acc, timesheet) => {
        // Create a unique key for each record
        const key = `${timesheet.weekStart}-${timesheet.weekEnd}-${timesheet.userName}-${timesheet.totalHours}-${timesheet.status || "N/A"}-${timesheet.notes || "N/A"}`

        if (!acc[key]) {
          acc[key] = {
            weekStart: timesheet.weekStart,
            weekEnd: timesheet.weekEnd,
            user_name: timesheet.userName,
            totalHours: timesheet.totalHours,
            status: timesheet.status || "N/A",
            notes: timesheet.notes || "N/A",
            projects: [], // Array to store projectName & phaseName
          }
        }

        // Add project details
        timesheet.projects.forEach((project) => {
          acc[key].projects.push(`${project.projectName} (${project.phaseName})`)
        })

        return acc
      }, {})

      // Convert object back to array and join project names
      const formattedData = Object.values(groupedData).map((item) => ({
        ...item,
        projectDetails: item.projects.join(", "), // Combine projects into a single string
      }))

      setReportData(formattedData)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const fetchReport = async () => {
    try {
      setLoading(true)
      const params = {}

      if (selectedUser) params.userId = selectedUser
      if (selectedProject) params.projectName = selectedProject

      if (Object.keys(params).length > 0) {
        const response = await UserApi.getReportByProject({ params })

        // Grouping logic
        const groupedData = response.data.data.reduce((acc, timesheet) => {
          // Create a unique key for each record
          const key = `${timesheet.weekStart}-${timesheet.weekEnd}-${timesheet.userName}-${timesheet.totalHours}-${timesheet.status || "N/A"}-${timesheet.notes || "N/A"}`

          if (!acc[key]) {
            acc[key] = {
              weekStart: timesheet.weekStart,
              weekEnd: timesheet.weekEnd,
              user_name: timesheet.userName,
              totalHours: timesheet.totalHours,
              status: timesheet.status || "N/A",
              notes: timesheet.notes || "N/A",
              projects: [], // Array to store projectName & phaseName
            }
          }

          // Add project details
          timesheet.projects.forEach((project) => {
            acc[key].projects.push(`${project.projectName} (${project.phaseName})`)
          })

          return acc
        }, {})

        // Convert object back to array and join project names
        const formattedData = Object.values(groupedData).map((item) => ({
          ...item,
          projectDetails: item.projects.join(", "), // Combine projects into a single string
        }))

        setReportData(formattedData)
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (filter === "project") {
      fetchProjects()
    } else if (filter === "user") {
      fetchUsers()
    }
  }, [filter])

  useEffect(() => {
    if ((selectedUser || selectedProject) && filter !== "date") {
      fetchReport()
    }
  }, [selectedUser, selectedProject])

  // Function to convert JSON data to CSV and trigger download
  const downloadCSV = () => {
    if (!reportData.length) {
      console.log("No report data to download.")
      return
    }

    // Create CSV header row from keys of the first object
    const headers = Object.keys(reportData[0])
    const csvRows = []
    csvRows.push(headers.join(","))

    // Loop through rows and create CSV rows
    reportData.forEach((row) => {
      const values = headers.map((header) => {
        const escaped = ("" + row[header]).replace(/"/g, '""')
        return `"${escaped}"`
      })
      csvRows.push(values.join(","))
    })

    // Combine rows with newlines
    const csvString = csvRows.join("\n")

    // Create a blob and temporary link to trigger download
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.setAttribute("download", "time-report.csv")
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight={600}>
        Time Report
      </Typography>
      <Grid container mt={3}>
        <Grid
          item
          xs={6}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "end",
          }}
        >
          <Grid item xs={6}>
            <CustomSelect
              selectedFilter={filter}
              options={filterOptions}
              value={filter}
              onChange={handleChange}
              placeholder="Select an option"
              label="Filter"
            />
          </Grid>

          <Grid item xs={5}>
            {filter === "project" && (
              <CustomSelect
                options={projects}
                value={selectedProject}
                onChange={handleProjectChange}
                placeholder="Select a project"
                label="Project"
                isLoading={loading}
              />
            )}
            {filter === "date" && (
              <Box sx={{ position: "relative",
              mt:"16%" }}>
                <WeekPicker selectedDate={selectedDate} 
                mb={0}
                onDateChange={handleDateChange} />
              </Box>
            )}
            {filter === "user" && (
              <CustomSelect
                options={user}
                value={selectedUser}
                onChange={handleUserChange}
                placeholder="Select a User"
                label="Users"
                isLoading={loading}
              />
            )}
          </Grid>
        </Grid>

        <Grid item xs={6} sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button variant="contained" color="primary" sx={{ mt: 4 }} onClick={downloadCSV}>
            Download CSV of Current Report
          </Button>
        </Grid>
      </Grid>
      {error && (
        <Box sx={{ mt: 2, color: "error.main" }}>
          <Typography>{error}</Typography>
        </Box>
      )}
      <Box sx={{ mt: 4 }}>
        <GenericTable data={reportData} columns={columns} showAction={false} loading={loading} isSearchShown={false} />
      </Box>
    </Box>
  )
}

export default TimeReport

